import { Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import womanEnjoyingTea from "@/assets/woman-enjoying-tea.jpg";
import coupleTeaMoment from "@/assets/couple-tea-moment.jpg";
import teaCandleRitual from "@/assets/tea-candle-ritual.jpg";
import pouringTeaRitual from "@/assets/pouring-tea-ritual.jpg";

// Updated social media posts with new lifestyle photos
const socialPosts = [
  {
    id: 1,
    platform: "instagram",
    image: womanEnjoyingTea,
    caption: "Mijn avondritueel met Lunssence 🌙✨",
    hashtag: "#LunssenceLife",
    likes: 247
  },
  {
    id: 2,
    platform: "instagram", 
    image: coupleTeaMoment,
    caption: "Samen genieten van pure rust 💫",
    hashtag: "#Lunssence",
    likes: 342
  },
  {
    id: 3,
    platform: "instagram",
    image: teaCandleRitual, 
    caption: "Perfect ritueel moment 🕯️",
    hashtag: "#LunssenceLife",
    likes: 289
  },
  {
    id: 4,
    platform: "instagram",
    image: pouringTeaRitual,
    caption: "De kunst van thee zetten ✨",
    hashtag: "#LunssenceLife",
    likes: 421
  }
];

export const Social = () => {
  return (
    <section id="social" className="py-20 bg-gradient-to-b from-background to-gold/5 relative overflow-hidden">
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c4a96a' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Word deel van 
            <span className="text-gold text-glow ml-3">#LunssenceLife</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Volg ons ritueel op Instagram & TikTok en deel jouw moment van balans. 
            Inspireer anderen met jouw Lunssence ervaring.
          </p>
        </div>

        {/* Social Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {socialPosts.map((post, index) => (
            <div 
              key={post.id}
              className="group relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Post Image */}
              <div className="aspect-square overflow-hidden">
                <img 
                  src={post.image}
                  alt={post.caption}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      post.platform === 'instagram' ? 'bg-gradient-to-tr from-purple-500 to-pink-500' : 'bg-black'
                    }`}>
                      {post.platform === 'instagram' ? (
                        <Instagram className="w-4 h-4 text-white" />
                      ) : (
                        <span className="text-white text-xs font-bold">T</span>
                      )}
                    </div>
                    <span className="text-white text-sm font-medium">
                      {post.likes} likes
                    </span>
                  </div>
                  <p className="text-white text-sm mb-1">
                    {post.caption}
                  </p>
                  <p className="text-gold font-semibold text-sm">
                    {post.hashtag}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Social Media Buttons */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://www.instagram.com/lunssence_herbal_tea?igsh=MWptbG85aGd3eXFvcQ=="
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <Button 
                variant="outline"
                size="lg"
                className="border-gold text-gold hover:bg-gold hover:text-primary-foreground transition-all duration-200 hover:scale-105 hover:shadow-glow group w-full"
              >
                <Instagram className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                Volg ons op Instagram
              </Button>
            </a>
            
            <a
              href="https://www.tiktok.com/@lunssence.herbal?_r=1&_t=ZG-9192I7ELuxV"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <Button 
                variant="outline"
                size="lg"
                className="border-gold text-gold hover:bg-gold hover:text-primary-foreground transition-all duration-200 hover:scale-105 hover:shadow-glow group w-full"
              >
                <div className="w-5 h-5 mr-2 rounded bg-current flex items-center justify-center group-hover:animate-pulse">
                  <span className="text-background text-xs font-bold">T</span>
                </div>
                Bekijk onze TikTok
              </Button>
            </a>
          </div>
          
          <p className="text-muted-foreground text-sm mt-4">
            Tag ons <span className="text-gold font-semibold">@lunssence_herbal_tea</span> en gebruik 
            <span className="text-gold font-semibold"> #LunssenceLife</span>
          </p>
        </div>
      </div>
    </section>
  );
};